import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Signal,
  TemplateRef,
  effect,
  input,
  output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ModalRef, ModalService } from '@vinnova/ui';
import { BehaviorSubject, Observable, forkJoin, of, switchMap } from 'rxjs';
import { ReadonlyState } from '../../../../core/models/form.model';
import { Worksite } from '../../../../core/models/question/question-worksite.model';
import { RendererFormsRepository } from '../../../../core/repositories/renderer-forms.repository';
import { BaseWorksiteService } from '../../../../core/services/base-worksite.service';
import { FinancierService } from '../../../../core/services/financier.service';
import { sortAlphabetically } from '../../../../shared/utils/sorting.utils';

@UntilDestroy()
@Component({
  selector: 'lib-financier',
  templateUrl: './financier.component.html',
  styleUrls: ['./financier.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FinancierComponent implements OnInit {
  oregModal: ModalRef;
  removeModal: ModalRef;
  financier: Worksite;
  financierPendingRemoval: Worksite;
  newFinanciers: Set<string>;

  financiers = new BehaviorSubject<Worksite[]>([]);
  financiers$ = this.financiers.asObservable();

  isMobile = input<boolean>(false);
  readOnlyState = input<boolean>(false);
  worksiteInactive = input<boolean>(false);
  maxSize = input<number>();
  parentWorksite = input<string>();
  ReadonlyState = ReadonlyState;
  worksiteProviderIds: string[] = [];
  parentWorksiteProviderId: string[] = [];
  addFinancierEmitter = output<string>();
  removeFinancierEmitter = output<string>();

  get allowedToAddMore() {
    if (!this.maxSize()) return true;
    return this.financiers.value.length < this.maxSize() + 1;
  }

  get financiersProviderIds() {
    return this.financiers.value.map((w) => w.providerId);
  }

  get combinedProviderIds() {
    this.fetchParentWorksiteProviderId();
    return [...this.financiersProviderIds, ...this.parentWorksiteProviderId];
  }

  initialSorting = true;
  isChangeRequest: boolean;
  isSharedView: boolean;
  showChanges: Signal<boolean>;

  constructor(
    private modalService: ModalService,
    private worksiteService: BaseWorksiteService,
    private financierService: FinancierService,
    private rendererFormRepository: RendererFormsRepository,
  ) {
    this.showChanges = toSignal(this.rendererFormRepository.showChanges$);
    effect(() => {
      this.financierService
        .selectForWorksite(this.parentWorksite())
        .pipe(
          switchMap((f) => {
            const worksitesObs: Observable<unknown>[] = [];
            for (const id of f) {
              worksitesObs.push(this.worksiteService.getWorksite(id));
            }
            return worksitesObs.length > 0 ? forkJoin(worksitesObs) : of([]);
          }),
          untilDestroyed(this),
        )
        .subscribe((f) => {
          let financiersValue = [];
          for (const financier of f) {
            if (financier) {
              financiersValue.push(financier);
            }
          }
          if (this.initialSorting) {
            financiersValue = sortAlphabetically(financiersValue);
            this.initialSorting = false;
          }
          this.financiers.next(financiersValue);
        });
    });
  }
  private fetchParentWorksiteProviderId(): void {
    if (!this.parentWorksite) {
      return;
    }
    this.worksiteService
      .getWorksite(this.parentWorksite())
      .pipe(untilDestroyed(this))
      .subscribe((parent) => {
        if (this.parentWorksiteProviderId.includes(parent.providerId)) {
          return;
        }
        this.parentWorksiteProviderId.push(parent.providerId);
      });
  }
  ngOnInit(): void {
    this.isChangeRequest = this.rendererFormRepository.getIsChangeRequest();
    this.isSharedView = this.rendererFormRepository.getIsSharedView();
    this.newFinanciers = this.financierService.getNewFinanciers(
      this.parentWorksite(),
    );
  }

  isForeign(id: string) {
    const financierIndex = this.financiers.value.findIndex((f) => f.id === id);
    if (financierIndex !== -1) {
      const worksite = this.financiers.value[financierIndex];
      return worksite.company?.country?.toLowerCase() !== 'se';
    }
  }

  onOpenOregModal(content: TemplateRef<unknown>) {
    this.oregModal = this.modalService.show(content, {
      backdrop: 'static',
      class: 'modal-xl modal-fullscreen-md-down',
    });
  }

  onCloseOregModal() {
    this.oregModal.hide();
  }

  onSelectedWorksite(financierWorksite: Worksite) {
    this.worksiteService.addFinancier(financierWorksite, this.parentWorksite());
    if (
      this.financiers.value.some((f) => f.id === financierWorksite.id) ||
      financierWorksite.id === this.parentWorksite()
    ) {
      //TODO Felhantering
      this.onCloseOregModal();
      return;
    }
    this.addFinancier(financierWorksite);
    this.newFinanciers.add(financierWorksite.id);
    this.financier = financierWorksite;
    this.onCloseOregModal();
  }

  onOpenRemoveWorksiteModal(
    content: TemplateRef<unknown>,
    financier: Worksite,
  ) {
    this.removeModal = this.modalService.show(content, {
      backdrop: 'static',
      class: 'modal-m modal-fullscreen-md-down',
    });
    this.financierPendingRemoval = financier;
  }

  onRemoveFinancier() {
    this.removeFinancierEmitter.emit(this.financierPendingRemoval.id);
    this.financierPendingRemoval = undefined;
    this.removeModal.hide();
  }

  onCloseRemoveModal() {
    this.financierPendingRemoval = undefined;
    this.removeModal.hide();
  }

  sortAlphabetically(a) {
    return a;
  }

  private addFinancier(worksite: Worksite) {
    this.addFinancierEmitter.emit(worksite.id);
  }

  trackByFn(index) {
    return index;
  }
}
